import React from 'react';

import { UxContext } from '@client/context/UxContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { ChannelMapEntry, DmxResolution, LinkedZoneDoc } from '@ledium/types';
import { DmxChannelMapper, type DmxChannelMapperRow, LinkTypeChip } from '@ledium/ui-common';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Popover, Skeleton, Switch, TextField, Typography } from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';

interface ZoneOption {
    id: string;
    name: string;
    parent?: string;
    order?: number;
    virtual?: true;
}

interface ZoneTreeNode extends ZoneOption {
    parent: string;
    order: number;
    children: ZoneTreeNode[];
}

function buildParentTree(zones: ZoneOption[]): ZoneTreeNode {
    const root: ZoneTreeNode = { id: 'root', name: 'Root', parent: '', order: 0, children: [] };
    const map: Record<string, ZoneTreeNode> = { root };
    zones.forEach((z) => {
        map[z.id] = { ...z, parent: z.parent ?? 'root', order: z.order ?? 0, children: [] };
    });
    zones.forEach((z) => {
        const parentId = z.parent ?? 'root';
        (map[parentId] ?? root).children.push(map[z.id]);
    });
    function sortChildren(node: ZoneTreeNode) {
        node.children.sort((a, b) => a.order - b.order);
        node.children.forEach(sortChildren);
    }
    sortChildren(root);
    return root;
}

interface ParentZonePickerProps {
    zones: ZoneOption[];
    zonesWithDevices: Set<string>;
    value: string;
    onChange: (id: string) => void;
}

function ParentZonePicker({ zones, zonesWithDevices, value, onChange }: ParentZonePickerProps) {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const tree = React.useMemo(() => buildParentTree(zones), [zones]);
    const [expanded, setExpanded] = React.useState<string[]>(() => ['root', ...zones.map((z) => z.id)]);

    React.useEffect(() => {
        setExpanded(['root', ...zones.map((z) => z.id)]);
    }, [zones]);

    const currentName = value === 'root' ? 'Root' : (zones.find((z) => z.id === value)?.name ?? value);

    function renderNode(node: ZoneTreeNode): React.ReactElement {
        const disabled = node.id !== 'root' && (zonesWithDevices.has(node.id) || !!node.virtual);
        const isCurrent = node.id === value;
        return (
            <TreeItem
                key={node.id}
                itemId={node.id}
                label={
                    <Box
                        role={disabled ? undefined : 'button'}
                        tabIndex={disabled ? undefined : 0}
                        sx={{ display: 'flex', alignItems: 'center', py: 0.5, cursor: disabled ? 'default' : 'pointer' }}
                        onClick={
                            disabled
                                ? undefined
                                : (e) => {
                                      e.stopPropagation();
                                      onChange(node.id);
                                      setAnchorEl(null);
                                  }
                        }
                        onKeyDown={
                            disabled
                                ? undefined
                                : (e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                          e.stopPropagation();
                                          onChange(node.id);
                                          setAnchorEl(null);
                                      }
                                  }
                        }
                    >
                        <Typography variant="body2" sx={{ color: disabled ? 'text.disabled' : 'text.primary', fontWeight: isCurrent ? 700 : 400 }}>
                            {node.name}
                        </Typography>
                    </Box>
                }
            >
                {node.children.map((child) => renderNode(child))}
            </TreeItem>
        );
    }

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {i18n('parent_zone')} <strong>{currentName}</strong>
            </Typography>
            <Button size="small" variant="outlined" onClick={(e) => setAnchorEl(e.currentTarget)}>
                {i18n('change_parent')}
            </Button>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { maxHeight: 'min(360px, 50vh)', overflowY: 'auto' } }}
            >
                <Box sx={{ p: 1, minWidth: 260 }}>
                    <SimpleTreeView
                        slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
                        expandedItems={expanded}
                        selectedItems={value}
                        onExpandedItemsChange={(_e, ids) => setExpanded(ids)}
                    >
                        {renderNode(tree)}
                    </SimpleTreeView>
                </Box>
            </Popover>
        </Box>
    );
}

interface UsedLightChannel {
    id: string;
    name: string;
    dmxAddress: number;
    resolution: DmxResolution;
}

export interface AddZoneFormData {
    name: string;
    description: string;
    parent: string;
}

export interface LinkedZoneConfig {
    zoneId: string;
    sourceZoneId: string;
}

interface AddEditZoneDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: AddZoneFormData) => Promise<void>;
    zones: ZoneOption[];
    zonesWithDevices: Set<string>;
    existingNames: string[];
    initialData?: AddZoneFormData;
    isEdit?: boolean;
    linkedZoneConfig?: LinkedZoneConfig;
}

function AddEditZoneDialog({
    open,
    onClose,
    onSave,
    zones,
    zonesWithDevices,
    existingNames,
    initialData,
    isEdit = false,
    linkedZoneConfig
}: AddEditZoneDialogProps) {
    const { handleAlert } = React.useContext(UxContext)!;
    const [name, setName] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [parent, setParent] = React.useState('root');
    const [saving, setSaving] = React.useState(false);
    const [nameError, setNameError] = React.useState('');
    const [formTouched, setFormTouched] = React.useState(false);

    const [link, setLink] = React.useState<LinkedZoneDoc | null>(null);
    const [linkEnabled, setLinkEnabled] = React.useState(true);
    const [sourceChannels, setSourceChannels] = React.useState<UsedLightChannel[]>([]);
    const [channelMap, setChannelMap] = React.useState<Record<string, number | undefined>>({});
    const [takenAddresses, setTakenAddresses] = React.useState<number[]>([]);
    const [testingChannelId, setTestingChannelId] = React.useState<string | null>(null);
    const [loadingMapping, setLoadingMapping] = React.useState(false);
    const [switchingType, setSwitchingType] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setName(initialData?.name ?? '');
            setDescription(initialData?.description ?? '');
            setParent(initialData?.parent ?? 'root');
            setNameError('');
            setLink(null);
            setSourceChannels([]);
            setChannelMap({});
            setTakenAddresses([]);
            setFormTouched(false);
            setSwitchingType(false);
        }
    }, [open, initialData]);

    React.useEffect(() => {
        if (!open || !linkedZoneConfig) {
            return;
        }
        setLoadingMapping(true);
        Promise.all([
            lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${linkedZoneConfig.sourceZoneId}`),
            lediumApi.get<UsedLightChannel[]>(`zones/${linkedZoneConfig.sourceZoneId}/lightChannels`),
            lediumApi.get<number[]>('lightChannels/occupiedAddresses')
        ])
            .then(([linksData, channelsData, occupiedData]) => {
                const foundLink = (linksData ?? []).find((l) => l.targetZoneId === linkedZoneConfig.zoneId) ?? null;
                if (foundLink) {
                    const ownAddresses = new Set(foundLink.channelMap.map((e) => e.dmxAddress));
                    setLink(foundLink);
                    setLinkEnabled(foundLink.enabled);
                    setChannelMap(Object.fromEntries(foundLink.channelMap.map((e) => [e.sourceChannelId, e.dmxAddress])));
                    setTakenAddresses((occupiedData ?? []).filter((addr) => !ownAddresses.has(addr)));
                }
                setSourceChannels(channelsData ?? []);
            })
            .finally(() => setLoadingMapping(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, linkedZoneConfig?.zoneId]);

    const orphanRows: DmxChannelMapperRow[] = (link?.channelMap ?? [])
        .filter((e) => !sourceChannels.find((c) => c.id === e.sourceChannelId))
        .map((e) => ({
            channelId: e.sourceChannelId,
            name: i18n('linked_project_deleted_channel') as string,
            currentDmxAddress: e.dmxAddress,
            resolution: e.resolution
        }));

    const allRows: DmxChannelMapperRow[] = [
        ...sourceChannels.map((c) => ({ channelId: c.id, name: c.name, currentDmxAddress: c.dmxAddress, resolution: c.resolution })),
        ...orphanRows
    ];

    async function handleTest(channelId: string, dmxAddress: number, resolution: number) {
        setTestingChannelId(channelId);
        await lediumApi.get(`lightChannels/testDmx/${dmxAddress}/${resolution}`);
        setTestingChannelId(null);
    }

    async function handleSwitchType() {
        if (!link || !linkedZoneConfig) {
            return;
        }
        const linkSnapshot = link;
        const newType: 'mirror' | 'independent' = linkSnapshot.type === 'mirror' ? 'independent' : 'mirror';

        async function doSwitch() {
            setSwitchingType(true);
            const updated = await lediumApi.patch<LinkedZoneDoc>(`linkedZones/${linkSnapshot._id}/type`, { type: newType });
            setSwitchingType(false);
            if (updated) {
                setLink(updated);
            }
        }

        if (linkSnapshot.type === 'mirror') {
            const sourceRunning = await lediumApi.get<{ name: string | null; pausedCount: number }>(`zones/${linkedZoneConfig.sourceZoneId}/runningProject`);
            if (sourceRunning?.name) {
                handleAlert({
                    title: i18n('linked_zone_switch_to_independent_title') as string,
                    message: i18n('linked_zone_switch_to_independent_running_source') as string,
                    okCallback: doSwitch
                });
                return;
            }
            await doSwitch();
        } else {
            const [virtualActive, sourceRunning] = await Promise.all([
                lediumApi.get<{ name: string | null; pausedCount: number }>(`zones/${linkSnapshot.targetZoneId}/runningProject`),
                lediumApi.get<{ name: string | null; pausedCount: number }>(`zones/${linkedZoneConfig.sourceZoneId}/runningProject`)
            ]);
            const runningOrPaused = (virtualActive?.name ? 1 : 0) + (virtualActive?.pausedCount ?? 0);
            const parts: string[] = [];
            if (runningOrPaused > 0) {
                parts.push(i18n('linked_zone_switch_to_mirror_running_virtual', runningOrPaused) as string);
            }
            if (sourceRunning?.name) {
                parts.push(i18n('linked_zone_switch_to_mirror_running_source') as string);
            }
            if (parts.length > 0) {
                handleAlert({
                    title: i18n('linked_zone_switch_to_mirror_title') as string,
                    message: parts.join(' '),
                    okCallback: doSwitch
                });
                return;
            }
            await doSwitch();
        }
    }

    function handleClose() {
        if (formTouched) {
            handleAlert({
                title: i18n('discard_section_changes_title') as string,
                message: i18n('discard_section_changes_message') as string,
                okCallback: onClose
            });
        } else {
            onClose();
        }
    }

    async function handleSave() {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError(i18n('this_field_is_required') as string);
            return;
        }
        if (existingNames.includes(trimmedName)) {
            setNameError(i18n('the_name_should_be_unique_X_is_already_used', trimmedName) as string);
            return;
        }
        setSaving(true);
        try {
            await onSave({ name: trimmedName, description: description.trim(), parent });
            if (link) {
                const channelMapEntries: ChannelMapEntry[] = allRows
                    .filter((row) => channelMap[row.channelId] !== undefined)
                    .map((row) => ({
                        sourceChannelId: row.channelId,
                        dmxAddress: channelMap[row.channelId]!,
                        resolution: row.resolution as DmxResolution
                    }));
                await lediumApi.put(`linkedZones/${link._id}`, { channelMap: channelMapEntries, enabled: linkEnabled });
            }
            onClose();
        } finally {
            setSaving(false);
        }
    }

    let chipLabel = 'linked_project_status_disabled';
    let chipColor: 'error' | 'success' | 'default' = 'default';
    if (link?.disabledReason === 'mapping_error') {
        chipLabel = 'linked_project_status_mapping_error';
        chipColor = 'error';
    } else if (linkEnabled) {
        chipLabel = 'linked_project_status_enabled';
        chipColor = 'success';
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
            <DialogTitle>{isEdit ? i18n('edit_zone') : i18n('zone_selector_add')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    sx={{ mt: '14px' }}
                    label={i18n('name')}
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        setNameError('');
                        setFormTouched(true);
                    }}
                    error={!!nameError}
                    helperText={nameError || undefined}
                    fullWidth
                />
                <TextField
                    label={i18n('description')}
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                        setFormTouched(true);
                    }}
                    fullWidth
                    multiline
                    rows={2}
                />
                <ParentZonePicker
                    zones={zones}
                    zonesWithDevices={zonesWithDevices}
                    value={parent}
                    onChange={(id) => {
                        setParent(id);
                        setFormTouched(true);
                    }}
                />
                {linkedZoneConfig && (
                    <>
                        <Divider />
                        {loadingMapping ? (
                            <Box sx={{ py: 1 }}>
                                <Skeleton height={32} />
                                <Skeleton height={32} />
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {i18n('linked_zone_type_label')}
                                    </Typography>
                                    {link && <LinkTypeChip type={link.type} />}
                                    {link && (
                                        <Button size="small" variant="outlined" onClick={handleSwitchType} disabled={switchingType}>
                                            {i18n('linked_zone_switch_type_button')}
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {i18n('linked_zone_status_label')}
                                    </Typography>
                                    {link && <Chip size="small" label={i18n(chipLabel)} color={chipColor} sx={{ height: 20, fontSize: '0.65rem' }} />}
                                    <Switch
                                        size="small"
                                        checked={linkEnabled}
                                        disabled={link?.disabledReason === 'mapping_error'}
                                        onChange={async (e) => {
                                            const nextEnabled = e.target.checked;
                                            if (link?.type === 'mirror' && linkedZoneConfig) {
                                                const running = await lediumApi.get<{ name: string | null; pausedCount: number }>(
                                                    `zones/${linkedZoneConfig.sourceZoneId}/runningProject`
                                                );
                                                if (running?.name) {
                                                    const titleKey = nextEnabled ? 'linked_zone_enable_running_title' : 'linked_zone_disable_running_title';
                                                    const messageKey = nextEnabled
                                                        ? 'linked_zone_enable_running_message'
                                                        : 'linked_zone_disable_running_message';
                                                    handleAlert({
                                                        title: i18n(titleKey) as string,
                                                        message: i18n(messageKey, running.name) as string,
                                                        okCallback: () => {
                                                            setLinkEnabled(nextEnabled);
                                                            setFormTouched(true);
                                                        }
                                                    });
                                                    return;
                                                }
                                            } else if (link?.type === 'independent' && !nextEnabled) {
                                                const active = await lediumApi.get<{ name: string | null; pausedCount: number }>(
                                                    `zones/${link.targetZoneId}/runningProject`
                                                );
                                                const runningOrPaused = (active?.name ? 1 : 0) + (active?.pausedCount ?? 0);
                                                if (runningOrPaused > 0) {
                                                    handleAlert({
                                                        title: i18n('linked_zone_disable_running_title') as string,
                                                        message: i18n('linked_zone_independent_disable_message', runningOrPaused) as string,
                                                        okCallback: () => {
                                                            setLinkEnabled(false);
                                                            setFormTouched(true);
                                                        }
                                                    });
                                                    return;
                                                }
                                            }
                                            setLinkEnabled(nextEnabled);
                                            setFormTouched(true);
                                        }}
                                    />
                                </Box>
                                <Divider />
                                <Typography variant="subtitle2">{i18n('linked_project_edit_mapping')}</Typography>
                                <DmxChannelMapper
                                    channels={allRows}
                                    value={channelMap}
                                    onChange={(map) => {
                                        setChannelMap(map);
                                        setFormTouched(true);
                                    }}
                                    onTest={handleTest}
                                    testingChannelId={testingChannelId}
                                    takenAddresses={takenAddresses}
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    {i18n('cancel')}
                </Button>
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                    {i18n('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export { AddEditZoneDialog };
